import React from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import { IS_CRM, IS_SERVICES } from '@/app/app-variant';
import { AppShell } from '@/app/AppShell';
import { HomeRoute } from '@/app/HomeRoute';
import { WEBSITE_DASHBOARD_ROLES, WEBSITE_ROLES } from '@/app/nav-config';
import { ProtectedRoute } from '@/app/ProtectedRoute';
import { RequireRole } from '@/app/RequireRole';
import { RouteError } from '@/app/RouteError';
import { ActivitiesInboxPage } from '@/features/activities/pages/ActivitiesInboxPage';
import { ActivityEditPage } from '@/features/activities/pages/ActivityEditPage';
import { AuditActivityImportsPage } from '@/features/activity-imports/pages/AuditActivityImportsPage';
import { ImportActivitiesPage } from '@/features/activity-imports/pages/ImportActivitiesPage';
import { AdditionalDiscountsListPage } from '@/features/additional-discounts/pages/AdditionalDiscountsListPage';
import { AgentTargetsListPage } from '@/features/agent-targets/pages/AgentTargetsListPage';
import { AirlinesListPage } from '@/features/airlines/pages/AirlinesListPage';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage';
import { BranchesListPage } from '@/features/branches/pages/BranchesListPage';
import { CallStatusesListPage } from '@/features/call-statuses/pages/CallStatusesListPage';
import { CallSubStatusesListPage } from '@/features/call-sub-statuses/pages/CallSubStatusesListPage';
import { CallSensePage } from '@/features/callsense/pages/CallSensePage';
import { CatalogApprovalsPage } from '@/features/catalog-approvals/pages/CatalogApprovalsPage';
import { CategoryPageDetailPage } from '@/features/category-pages/pages/CategoryPageDetailPage';
import { CategoryPagesListPage } from '@/features/category-pages/pages/CategoryPagesListPage';
import { CitiesListPage } from '@/features/cities/pages/CitiesListPage';
import { CountriesListPage } from '@/features/countries/pages/CountriesListPage';
import { AuditCruiseImportsPage } from '@/features/cruise-imports/pages/AuditCruiseImportsPage';
import { ImportCruisesPage } from '@/features/cruise-imports/pages/ImportCruisesPage';
import { CruiseEditPage } from '@/features/cruises/pages/CruiseEditPage';
import { CruisesListPage } from '@/features/cruises/pages/CruisesListPage';
import { CuisinesListPage } from '@/features/cuisines/pages/CuisinesListPage';
import { CurrenciesListPage } from '@/features/currencies/pages/CurrenciesListPage';
import { CustomerPdfPage } from '@/features/customer-pdf/pages/CustomerPdfPage';
import { CustomersListPage } from '@/features/customers/pages/CustomersListPage';
import { CustomerStepperPage } from '@/features/customers/pages/CustomerStepperPage';
import { DepartmentsListPage } from '@/features/departments/pages/DepartmentsListPage';
import { DestinationPageDetailPage } from '@/features/destination-pages/pages/DestinationPageDetailPage';
import { DestinationPagesListPage } from '@/features/destination-pages/pages/DestinationPagesListPage';
import { DestinationsListPage } from '@/features/destinations/pages/DestinationsListPage';
import { DiscountCouponsListPage } from '@/features/discount-coupons/pages/DiscountCouponsListPage';
import { DiscountsListPage } from '@/features/discounts/pages/DiscountsListPage';
import { FixedDepartureDetailPage } from '@/features/fixed-departures/pages/FixedDepartureDetailPage';
import { FixedDeparturePickerPage } from '@/features/fixed-departures/pages/FixedDeparturePickerPage';
import { FixedDeparturesListPage } from '@/features/fixed-departures/pages/FixedDeparturesListPage';
import { NewFixedDeparturePage } from '@/features/fixed-departures/pages/NewFixedDeparturePage';
import { FollowupReasonsListPage } from '@/features/followup-reasons/pages/FollowupReasonsListPage';
import { HeroInclusionsListPage } from '@/features/hero-inclusions/pages/HeroInclusionsListPage';
import { AuditHotelImportsPage } from '@/features/hotel-imports/pages/AuditHotelImportsPage';
import { ImportHotelsPage } from '@/features/hotel-imports/pages/ImportHotelsPage';
import { HotelEditPage } from '@/features/hotels/pages/HotelEditPage';
import { HotelsListPage } from '@/features/hotels/pages/HotelsListPage';
import { InclusionsExclusionsListPage } from '@/features/inclusions-exclusions/pages/InclusionsExclusionsListPage';
import { InsuranceEditPage } from '@/features/insurance/pages/InsuranceEditPage';
import { InsuranceListPage } from '@/features/insurance/pages/InsuranceListPage';
import { AuditInsuranceImportsPage } from '@/features/insurance-imports/pages/AuditInsuranceImportsPage';
import { ImportInsurancePage } from '@/features/insurance-imports/pages/ImportInsurancePage';
import { AgentReportCardPage } from '@/features/invoices/pages/AgentReportCardPage';
import { BalanceDueReportPage } from '@/features/invoices/pages/BalanceDueReportPage';
import { BankReconcilePage } from '@/features/invoices/pages/BankReconcilePage';
import { BankStatementPage } from '@/features/invoices/pages/BankStatementPage';
import { ClientOutstandingPage } from '@/features/invoices/pages/ClientOutstandingPage';
import { CreditNotePrintPage } from '@/features/invoices/pages/CreditNotePrintPage';
import { CreditNotesListPage } from '@/features/invoices/pages/CreditNotesListPage';
import { DepositBookingPage } from '@/features/invoices/pages/DepositBookingPage';
import { GstOutputRegisterPage } from '@/features/invoices/pages/GstOutputRegisterPage';
import { InvoiceCreatePage } from '@/features/invoices/pages/InvoiceCreatePage';
import { InvoiceDetailPage } from '@/features/invoices/pages/InvoiceDetailPage';
import { InvoicesListPage } from '@/features/invoices/pages/InvoicesListPage';
import { MonthlySaleReportPage } from '@/features/invoices/pages/MonthlySaleReportPage';
import { OperationPendingPage } from '@/features/invoices/pages/OperationPendingPage';
import { ReceiptPrintPage } from '@/features/invoices/pages/ReceiptPrintPage';
import { SalesRegisterPage } from '@/features/invoices/pages/SalesRegisterPage';
import { SupplierChangeApprovalsPage } from '@/features/invoices/pages/SupplierChangeApprovalsPage';
import { SupplierLedgerPage } from '@/features/invoices/pages/SupplierLedgerPage';
import { SupplierPaidOutstandingPage } from '@/features/invoices/pages/SupplierPaidOutstandingPage';
import { SupplierPaymentSchedulePage } from '@/features/invoices/pages/SupplierPaymentSchedulePage';
import { SupplierPaymentsPage } from '@/features/invoices/pages/SupplierPaymentsPage';
import { TcsRegisterPage } from '@/features/invoices/pages/TcsRegisterPage';
import { ItinerariesListPage } from '@/features/itineraries/pages/ItinerariesListPage';
import { ItineraryDetailPage } from '@/features/itineraries/pages/ItineraryDetailPage';
import { NewItineraryPage } from '@/features/itineraries/pages/NewItineraryPage';
import { RepriceApprovalsPage } from '@/features/itineraries/pages/RepriceApprovalsPage';
import { VoidApprovalsPage } from '@/features/itineraries/pages/VoidApprovalsPage';
import { LanguagesListPage } from '@/features/languages/pages/LanguagesListPage';
import { AuditImportsPage } from '@/features/lead-imports/pages/AuditImportsPage';
import { ImportLeadsPage } from '@/features/lead-imports/pages/ImportLeadsPage';
import { ExitUnassignedLeadsPage } from '@/features/leads/pages/ExitUnassignedLeadsPage';
import { LeadApprovalsPage } from '@/features/leads/pages/LeadApprovalsPage';
import { LeadDetailPage } from '@/features/leads/pages/LeadDetailPage';
import { LeadFormPage } from '@/features/leads/pages/LeadFormPage';
import { LeadInboxPage } from '@/features/leads/pages/LeadInboxPage';
import { LeadParsingPage } from '@/features/leads/pages/LeadParsingPage';
import { LostReasonsListPage } from '@/features/lost-reasons/pages/LostReasonsListPage';
import { MarketingCostHeadsPage } from '@/features/marketing-reports/pages/MarketingCostHeadsPage';
import { MarketingReportPage } from '@/features/marketing-reports/pages/MarketingReportPage';
import { MarketingTagsListPage } from '@/features/marketing-tags/pages/MarketingTagsListPage';
import { MarkupProfilesListPage } from '@/features/markup-profiles/pages/MarkupProfilesListPage';
import { MealPlansListPage } from '@/features/meal-plans/pages/MealPlansListPage';
import { AuditMealServiceImportsPage } from '@/features/meal-service-imports/pages/AuditMealServiceImportsPage';
import { ImportMealServicesPage } from '@/features/meal-service-imports/pages/ImportMealServicesPage';
import { MealServiceEditPage } from '@/features/meal-services/pages/MealServiceEditPage';
import { MealServicesListPage } from '@/features/meal-services/pages/MealServicesListPage';
import { MetaCapiPage } from '@/features/meta-capi/pages/MetaCapiPage';
import { MyProfilePage } from '@/features/my-profile/pages/MyProfilePage';
import { MyNotificationsPage } from '@/features/notifications/pages/MyNotificationsPage';
import { NotificationsPage } from '@/features/notifications/pages/NotificationsPage';
import { OccupancyTypesListPage } from '@/features/occupancy-types/pages/OccupancyTypesListPage';
import { ConvertedLeadsPage } from '@/features/ops-cases/pages/ConvertedLeadsPage';
import { FundRequestsPage } from '@/features/ops-cases/pages/FundRequestsPage';
import { OpsApprovalsPage } from '@/features/ops-cases/pages/OpsApprovalsPage';
import { OpsCasePage } from '@/features/ops-cases/pages/OpsCasePage';
import { PackageDetailPage } from '@/features/packages/pages/PackageDetailPage';
import { PackagesListPage } from '@/features/packages/pages/PackagesListPage';
import { ProductsDashboardPage } from '@/features/products-dashboard/pages/ProductsDashboardPage';
import { QueryMediumsListPage } from '@/features/query-mediums/pages/QueryMediumsListPage';
import { QuerySourcesListPage } from '@/features/query-sources/pages/QuerySourcesListPage';
import { QueryStatusesListPage } from '@/features/query-statuses/pages/QueryStatusesListPage';
import { QuerySubSourcesListPage } from '@/features/query-sub-sources/pages/QuerySubSourcesListPage';
import { QuerySubStatusesListPage } from '@/features/query-sub-statuses/pages/QuerySubStatusesListPage';
import { QueryTypesListPage } from '@/features/query-types/pages/QueryTypesListPage';
import { DailyTrackerPage } from '@/features/reports/pages/DailyTrackerPage';
import { RolesListPage } from '@/features/roles/pages/RolesListPage';
import { RoomTypesListPage } from '@/features/room-types/pages/RoomTypesListPage';
import { SchedulerLogsPage } from '@/features/scheduler/pages/SchedulerLogsPage';
import { SecretsListPage } from '@/features/secrets/pages/SecretsListPage';
import { SessionAuditPage } from '@/features/session-audit/pages/SessionAuditPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { StatesListPage } from '@/features/states/pages/StatesListPage';
import { StatusPage } from '@/features/status/pages/StatusPage';
import { SubBranchesListPage } from '@/features/sub-branches/pages/SubBranchesListPage';
import { SuppliersListPage } from '@/features/suppliers/pages/SuppliersListPage';
import { TboGeographyPage } from '@/features/tbo/pages/TboGeographyPage';
import { TboAuditListPage } from '@/features/tbo-audit/pages/TboAuditListPage';
import { AuditTransferImportsPage } from '@/features/transfer-imports/pages/AuditTransferImportsPage';
import { ImportTransfersPage } from '@/features/transfer-imports/pages/ImportTransfersPage';
import { TransferEditPage } from '@/features/transfers/pages/TransferEditPage';
import { TransfersInboxPage } from '@/features/transfers/pages/TransfersInboxPage';
import { TicketingActionPage } from '@/features/travel-bookings/pages/TicketingActionPage';
import { TicketingActivitiesPage } from '@/features/travel-bookings/pages/TicketingActivitiesPage';
import { TicketingDropOffsPage } from '@/features/travel-bookings/pages/TicketingDropOffsPage';
import { TicketingFlightsPage } from '@/features/travel-bookings/pages/TicketingFlightsPage';
import { TicketingHotelsPage } from '@/features/travel-bookings/pages/TicketingHotelsPage';
import { TicketingReportsPage } from '@/features/travel-bookings/pages/TicketingReportsPage';
import { TravelBookingDetailPage } from '@/features/travel-bookings/pages/TravelBookingDetailPage';
import { TripTypesListPage } from '@/features/trip-types/pages/TripTypesListPage';
import { UserDetailPage } from '@/features/users/pages/UserDetailPage';
import { UsersListPage } from '@/features/users/pages/UsersListPage';
import { VehicleTypesListPage } from '@/features/vehicle-types/pages/VehicleTypesListPage';
import { AuditVisaImportsPage } from '@/features/visa-imports/pages/AuditVisaImportsPage';
import { ImportVisasPage } from '@/features/visa-imports/pages/ImportVisasPage';
import { VisaEditPage } from '@/features/visas/pages/VisaEditPage';
import { VisasListPage } from '@/features/visas/pages/VisasListPage';
import { WebsiteApprovalsPage } from '@/features/website-approvals/pages/WebsiteApprovalsPage';
import { BlogFormPage } from '@/features/website-cms/blog/BlogFormPage';
import { BlogsListPage } from '@/features/website-cms/blog/BlogsListPage';
import { BlogCategoriesListPage } from '@/features/website-cms/blog-categories/pages/BlogCategoriesListPage';
import { NavigationPage } from '@/features/website-cms/navigation/NavigationPage';
import { CareerEditor } from '@/features/website-cms/pages/CareerEditor';
import {
  BEST_PLACES_PAGES_CONFIG,
  BEST_TIME_PAGES_CONFIG,
  LANDING_PAGES_CONFIG,
  TOURISM_BOARDS_CONFIG,
} from '@/features/website-cms/pages/page-screen-config';
import { PageEditor } from '@/features/website-cms/pages/PageEditor';
import { PagesListPage } from '@/features/website-cms/pages/PagesListPage';
import { WebsiteDashboardPage } from '@/features/website-dashboard/pages/WebsiteDashboardPage';
import { WelcomeEditorPage } from '@/features/welcome/pages/WelcomeEditorPage';
import { WelcomePage } from '@/features/welcome/pages/WelcomePage';
import { WhatsAppInboxPage } from '@/features/whatsapp/pages/WhatsAppInboxPage';
import { WhatsAppLogPage } from '@/features/whatsapp-log/pages/WhatsAppLogPage';
import { PlaceholderPage } from '@/pages/placeholder';

/** Route guard matching `requirePermission` on the nav item. */
const perm = (code: string): React.JSX.Element => <RequireRole permission={code} />;

/**
 * Router scaffold.
 *
 * Every feature route is permission- or role-gated to match
 * `apps/admin/src/app/nav-config.tsx`. Sidebar + ⌘K use the same
 * permission codes via `canAccessNavItem()`; these guards are the
 * second line of defence for direct URL entry.
 */
const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <RouteError />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
    errorElement: <RouteError />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
    errorElement: <RouteError />,
  },
  {
    element: <ProtectedRoute />,
    errorElement: <RouteError />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <HomeRoute /> },
          { path: 'profile', element: <MyProfilePage /> },
          // Welcome Utazzer - the shared post-login landing on BOTH the CRM and
          // the Services (accounts) build. The welcome gate in AppShell forces
          // every signed-in user here first and holds navigation until they
          // scroll to the end, so this route must exist on both builds.
          { path: 'welcome', element: <WelcomePage /> },
          { path: 'notifications/me', element: <MyNotificationsPage /> },

          // Customers: available on BOTH builds - the CRM ops surface AND the
          // Services/accounts build, because an invoice is billed to a customer,
          // so FINANCE must be able to pick/create them. Gated by
          // ops.customers.read, which FINANCE holds by default.
          {
            element: perm('ops.customers.read'),
            children: [
              { path: 'customers', element: <CustomersListPage /> },
              { path: 'customers/new', element: <CustomerStepperPage /> },
              { path: 'customers/:id', element: <CustomerStepperPage /> },
            ],
          },

          // Finance-permitted invoicing dependencies (suppliers, reference masters,
          // settings): mounted on BOTH builds; the perm gate controls access.
          {
            element: perm('inv.suppliers.read'),
            children: [{ path: 'suppliers', element: <SuppliersListPage /> }],
          },
          {
            element: perm('inv.airlines.read'),
            children: [{ path: 'airlines', element: <AirlinesListPage /> }],
          },
          {
            element: perm('geo.countries.read'),
            children: [{ path: 'countries', element: <CountriesListPage /> }],
          },
          {
            element: perm('geo.states.read'),
            children: [{ path: 'states', element: <StatesListPage /> }],
          },
          {
            element: perm('geo.cities.read'),
            children: [{ path: 'cities', element: <CitiesListPage /> }],
          },
          {
            element: perm('geo.currencies.read'),
            children: [{ path: 'currencies', element: <CurrenciesListPage /> }],
          },
          {
            element: perm('crm.branches.read'),
            children: [
              { path: 'branches', element: <BranchesListPage /> },
              { path: 'sub-branches', element: <SubBranchesListPage /> },
            ],
          },
          {
            element: perm('crm.query_sources.read'),
            children: [{ path: 'query-sources', element: <QuerySourcesListPage /> }],
          },
          {
            element: perm('sys.settings.read'),
            children: [{ path: 'settings', element: <SettingsPage /> }],
          },

          // ---- CRM-only routes ----------------------------------------------
          // Everything in this spread is mounted ONLY on the CRM build
          // (utazzocrm.com). On the Services/accounts build (utazzoservices.com)
          // these routes do not exist at all, so no CRM screen (leads, catalog,
          // website, reports) is reachable there by direct URL even for ADMIN -
          // the hard counterpart to the nav hiding in app-variant.ts. The
          // invoicing surface is the mirror IS_SERVICES spread further down, and
          // the catch-all at the end sends stray paths on Services to /invoices.
          ...(IS_CRM
            ? [
                // Website Dashboard. Role gate mirrors the nav item
                // (WEBSITE_DASHBOARD_ROLES = ADMIN + WEBSITE_MANAGER and every
                // role under it); the two aggregates it reads already allow these
                // roles server-side.
                {
                  element: <RequireRole allow={WEBSITE_DASHBOARD_ROLES} />,
                  children: [{ path: 'website-dashboard', element: <WebsiteDashboardPage /> }],
                },
                // Products (catalog) dashboard. Reads /dashboard/products-breakdown,
                // which allows ADMIN + OPS + CONTENT_EDITOR server-side; gate mirrors it.
                {
                  element: <RequireRole allow={['ADMIN', 'OPS', 'CONTENT_EDITOR']} />,
                  children: [{ path: 'products-dashboard', element: <ProductsDashboardPage /> }],
                },
                // Welcome editor - gated by the web.welcome module (read opens
                // it, write saves on the API). The Welcome page itself is the
                // shared route above; only the editor is CRM-only.
                {
                  element: <RequireRole permission="web.welcome.read" />,
                  children: [{ path: 'welcome-page', element: <WelcomeEditorPage /> }],
                },
                {
                  element: perm('ops.leads.write'),
                  children: [{ path: 'leads/approvals', element: <LeadApprovalsPage /> }],
                },
                {
                  element: perm('ops.lead_imports.read'),
                  children: [
                    { path: 'leads/imports', element: <AuditImportsPage /> },
                    { path: 'leads/imports/new', element: <ImportLeadsPage /> },
                    { path: 'leads/imports/jobs/:jobId', element: <ImportLeadsPage /> },
                  ],
                },
                {
                  element: perm('ops.scheduler.read'),
                  children: [{ path: 'scheduler', element: <SchedulerLogsPage /> }],
                },
                {
                  element: perm('ops.status.read'),
                  children: [{ path: 'status', element: <StatusPage /> }],
                },
                {
                  element: perm('sys.roles.read'),
                  children: [{ path: 'catalog/approvals', element: <CatalogApprovalsPage /> }],
                },
                {
                  // Website content review queue. Gated on destination-page read
                  // rather than a new code: everyone who can review website content
                  // already holds it, and it keeps the queue out of sales roles.
                  element: perm('web.destination_pages.read'),
                  children: [{ path: 'website/approvals', element: <WebsiteApprovalsPage /> }],
                },
                {
                  // Lead parsing has its own dedicated permission (admin-only by
                  // default). Kept in a separate block so plain `ops.leads.read`
                  // holders can't reach /leads/parsing by direct URL.
                  element: perm('ops.lead_parsing.read'),
                  children: [{ path: 'leads/parsing', element: <LeadParsingPage /> }],
                },
                {
                  // Exit-unassigned queue: released leads from departed agents.
                  // Own dedicated code (sales_lead + admin) so plain
                  // `ops.leads.read` holders can't reach it by direct URL. Static
                  // path outranks `leads/:id` so it never collides with detail.
                  element: perm('ops.exit_unassigned.read'),
                  children: [
                    { path: 'leads/exit-unassigned', element: <ExitUnassignedLeadsPage /> },
                  ],
                },
                {
                  // Operations inbox of converted leads (modules/ops-cases).
                  element: perm('ops.converted_leads.read'),
                  children: [
                    { path: 'operations/converted-leads', element: <ConvertedLeadsPage /> },
                    { path: 'operations/converted-leads/:id', element: <OpsCasePage /> },
                  ],
                },
                {
                  // Admin-only: price-increase + itinerary-change approvals.
                  element: <RequireRole allow={['ADMIN']} />,
                  children: [{ path: 'operations/approvals', element: <OpsApprovalsPage /> }],
                },
                {
                  // Website roles may hold ops.leads.read but are scoped to the
                  // website module, so deny them the lead screens by role too (matches
                  // the nav item's hideForRoles) - otherwise the URL is reachable.
                  element: <RequireRole permission="ops.leads.read" denyRoles={WEBSITE_ROLES} />,
                  children: [
                    { path: 'leads', element: <LeadInboxPage /> },
                    { path: 'leads/new', element: <LeadFormPage /> },
                    { path: 'leads/:id', element: <LeadDetailPage /> },
                    { path: 'leads/:id/edit', element: <LeadFormPage /> },
                  ],
                },
                {
                  // Reports expose org-wide agent performance. Gated on a dedicated
                  // permission (admin-only by default, grantable from /roles).
                  element: perm('ops.lead_reports.read'),
                  children: [{ path: 'reports/leads', element: <DailyTrackerPage /> }],
                },
                {
                  // Marketing / attribution reporting. Own permission so channel
                  // reporting can be granted without org-wide agent performance.
                  element: perm('ops.marketing_reports.read'),
                  children: [{ path: 'reports/marketing', element: <MarketingReportPage /> }],
                },
                {
                  element: perm('ops.callsense.read'),
                  children: [{ path: 'callsense', element: <CallSensePage /> }],
                },
                // (customers moved to the always-on section above so it is also
                // available on the Services/accounts build for FINANCE)
                {
                  // The fixed-departure TEMPLATE master is a management surface, so
                  // it's gated on WRITE (create / edit / delete templates). Admin holds
                  // it by default; anyone granted ops.fixed_departures.write reaches it.
                  // The per-lead picker below stays on the READ permission so the sales
                  // flow keeps working for read-only holders.
                  element: perm('ops.fixed_departures.write'),
                  children: [
                    { path: 'fixed-departures', element: <FixedDeparturesListPage /> },
                    { path: 'fixed-departures/new', element: <NewFixedDeparturePage /> },
                    { path: 'fixed-departures/:id', element: <FixedDepartureDetailPage /> },
                  ],
                },
                {
                  element: perm('ops.fixed_departures.read'),
                  children: [
                    {
                      path: 'leads/:leadId/fixed-departures',
                      element: <FixedDeparturePickerPage />,
                    },
                  ],
                },
                {
                  element: perm('ops.itineraries.read'),
                  children: [
                    { path: 'itineraries', element: <ItinerariesListPage /> },
                    { path: 'itineraries/new', element: <NewItineraryPage /> },
                    // Static path outranks `itineraries/:id` so it never collides
                    // with detail. Queue itself is reviewer-scoped server-side
                    // (ADMIN / SALES_LEAD reviewers, SALES_AGENT requester-only
                    // read view) - same read permission as the itineraries module.
                    { path: 'itineraries/reprice-approvals', element: <RepriceApprovalsPage /> },
                    { path: 'itineraries/void-approvals', element: <VoidApprovalsPage /> },
                    { path: 'itineraries/:id', element: <ItineraryDetailPage /> },
                  ],
                },
                {
                  // Website flight + hotel bookings and the hold queue. ADMIN only,
                  // by role: the detail shows passport numbers and the actions move
                  // guest money. Mirrors `requireRole` on the nav item.
                  element: <RequireRole allow={['ADMIN']} />,
                  children: [
                    { path: 'ticketing/action', element: <TicketingActionPage /> },
                    { path: 'ticketing/flights', element: <TicketingFlightsPage /> },
                    { path: 'ticketing/hotels', element: <TicketingHotelsPage /> },
                    { path: 'ticketing/drop-offs', element: <TicketingDropOffsPage /> },
                    { path: 'ticketing/activities', element: <TicketingActivitiesPage /> },
                    { path: 'ticketing/reports', element: <TicketingReportsPage /> },
                    // The old list URL, kept for bookmarks and notification links.
                    { path: 'bookings', element: <Navigate to="/ticketing/action" replace /> },
                    { path: 'bookings/:id', element: <TravelBookingDetailPage /> },
                  ],
                },
                {
                  element: perm('ops.tbo.read'),
                  children: [{ path: 'tbo', element: <TboGeographyPage /> }],
                },
                {
                  element: perm('inv.hotels.read'),
                  children: [
                    { path: 'hotels', element: <HotelsListPage /> },
                    { path: 'hotels/new', element: <HotelEditPage /> },
                    { path: 'hotels/:id/edit', element: <HotelEditPage /> },
                    { path: 'hotels/imports', element: <AuditHotelImportsPage /> },
                    { path: 'hotels/imports/new', element: <ImportHotelsPage /> },
                    { path: 'hotels/imports/jobs/:jobId', element: <ImportHotelsPage /> },
                  ],
                },
                {
                  // Cruises are v1 modeled as a "stay" alongside hotels and reuse
                  // the hotels read/write permission - no dedicated inv.cruises.*
                  // permission is seeded yet.
                  element: perm('inv.hotels.read'),
                  children: [
                    { path: 'cruises', element: <CruisesListPage /> },
                    { path: 'cruises/new', element: <CruiseEditPage /> },
                    { path: 'cruises/:id/edit', element: <CruiseEditPage /> },
                    { path: 'cruises/imports', element: <AuditCruiseImportsPage /> },
                    { path: 'cruises/imports/new', element: <ImportCruisesPage /> },
                    { path: 'cruises/imports/jobs/:jobId', element: <ImportCruisesPage /> },
                  ],
                },
                {
                  element: perm('inv.activities.read'),
                  children: [
                    { path: 'activities', element: <ActivitiesInboxPage /> },
                    { path: 'activities/new', element: <ActivityEditPage /> },
                    { path: 'activities/:id/edit', element: <ActivityEditPage /> },
                    { path: 'activities/imports', element: <AuditActivityImportsPage /> },
                    { path: 'activities/imports/new', element: <ImportActivitiesPage /> },
                    { path: 'activities/imports/jobs/:jobId', element: <ImportActivitiesPage /> },
                  ],
                },
                {
                  element: perm('inv.transfers.read'),
                  children: [
                    { path: 'transfers', element: <TransfersInboxPage /> },
                    { path: 'transfers/new', element: <TransferEditPage /> },
                    { path: 'transfers/:id/edit', element: <TransferEditPage /> },
                    { path: 'transfers/imports', element: <AuditTransferImportsPage /> },
                    { path: 'transfers/imports/new', element: <ImportTransfersPage /> },
                    { path: 'transfers/imports/jobs/:jobId', element: <ImportTransfersPage /> },
                  ],
                },
                {
                  element: perm('inv.visas.read'),
                  children: [
                    { path: 'visas', element: <VisasListPage /> },
                    { path: 'visas/new', element: <VisaEditPage /> },
                    { path: 'visas/:id/edit', element: <VisaEditPage /> },
                    { path: 'visas/imports', element: <AuditVisaImportsPage /> },
                    { path: 'visas/imports/new', element: <ImportVisasPage /> },
                    { path: 'visas/imports/jobs/:jobId', element: <ImportVisasPage /> },
                  ],
                },
                {
                  element: perm('inv.insurance.read'),
                  children: [
                    { path: 'insurance', element: <InsuranceListPage /> },
                    { path: 'insurance/new', element: <InsuranceEditPage /> },
                    { path: 'insurance/:id/edit', element: <InsuranceEditPage /> },
                    { path: 'insurance/imports', element: <AuditInsuranceImportsPage /> },
                    { path: 'insurance/imports/new', element: <ImportInsurancePage /> },
                    { path: 'insurance/imports/jobs/:jobId', element: <ImportInsurancePage /> },
                  ],
                },
                {
                  element: perm('inv.meal_services.read'),
                  children: [
                    { path: 'meal-services', element: <MealServicesListPage /> },
                    { path: 'meal-services/new', element: <MealServiceEditPage /> },
                    { path: 'meal-services/:id/edit', element: <MealServiceEditPage /> },
                    { path: 'meal-services/imports', element: <AuditMealServiceImportsPage /> },
                    { path: 'meal-services/imports/new', element: <ImportMealServicesPage /> },
                    {
                      path: 'meal-services/imports/jobs/:jobId',
                      element: <ImportMealServicesPage />,
                    },
                  ],
                },
                {
                  element: perm('inv.destinations.read'),
                  children: [{ path: 'destinations-master', element: <DestinationsListPage /> }],
                },
                {
                  element: perm('itin.meal_plans.read'),
                  children: [{ path: 'meal-plans', element: <MealPlansListPage /> }],
                },
                {
                  element: perm('itin.room_types.read'),
                  children: [{ path: 'room-types', element: <RoomTypesListPage /> }],
                },
                {
                  element: perm('itin.occupancy_types.read'),
                  children: [{ path: 'occupancy-types', element: <OccupancyTypesListPage /> }],
                },
                {
                  element: perm('itin.vehicle_types.read'),
                  children: [{ path: 'vehicle-types', element: <VehicleTypesListPage /> }],
                },
                {
                  element: perm('itin.languages.read'),
                  children: [{ path: 'languages', element: <LanguagesListPage /> }],
                },
                {
                  element: perm('itin.cuisines.read'),
                  children: [{ path: 'cuisines', element: <CuisinesListPage /> }],
                },
                {
                  // Global/destination markup profiles drive itinerary pricing;
                  // admin-only lever (consolidates the retired /markups screen).
                  element: <RequireRole allow={['ADMIN']} />,
                  children: [{ path: 'markup-profiles', element: <MarkupProfilesListPage /> }],
                },
                {
                  // Admin-only session/auth audit trail viewer. Hidden from one
                  // specific admin account (prod-only) per request.
                  element: (
                    <RequireRole allow={['ADMIN']} denyEmails={['shambhu@utazzoholidays.com']} />
                  ),
                  children: [{ path: 'session-audit', element: <SessionAuditPage /> }],
                },
                {
                  // Admin-only TBO API audit log viewer (every flight/hotel call to
                  // TBO, recorded by the non-blocking recorder). Read-only.
                  element: <RequireRole allow={['ADMIN']} />,
                  children: [{ path: 'tbo-audit', element: <TboAuditListPage /> }],
                },
                {
                  // Full-page WhatsApp inbox - same scope as the floating widget.
                  element: perm('ops.whatsapp.read'),
                  children: [{ path: 'whatsapp', element: <WhatsAppInboxPage /> }],
                },
                {
                  // Admin-only WhatsApp delivery log. Agents see no send failures in
                  // the chat widget; this is where they are answerable.
                  element: <RequireRole allow={['ADMIN']} />,
                  children: [{ path: 'whatsapp-log', element: <WhatsAppLogPage /> }],
                },
              ]
            : []),
          // GST invoicing (admin + finance) lives ONLY on the Services build
          // (utazzoservices.com) and is absent from the CRM build entirely.
          // Mirrors the invoicing API's @Roles('ADMIN','FINANCE') guard.
          ...(IS_SERVICES
            ? [
                {
                  // Accounts: supplier fund requests raised by operations. Paid
                  // from the Services app only (hidden on the CRM, see
                  // SERVICES_ONLY_ITEM_IDS in app-variant.ts).
                  element: perm('ops.fund_requests.read'),
                  children: [{ path: 'operations/fund-requests', element: <FundRequestsPage /> }],
                },
                {
                  element: <RequireRole allow={['ADMIN', 'FINANCE']} />,
                  children: [
                    { path: 'invoices', element: <InvoicesListPage /> },
                    { path: 'invoices/new', element: <InvoiceCreatePage /> },
                    { path: 'invoices/:id/edit', element: <InvoiceCreatePage /> },
                    // Customer payment receipt print sheet.
                    { path: 'invoices/:id/receipt/print', element: <ReceiptPrintPage /> },
                    // Credit-note print sheet. Registered BEFORE `invoices/:id` so the
                    // literal `credit-notes` segment is not swallowed by the :id param.
                    { path: 'invoices/credit-notes/:id/print', element: <CreditNotePrintPage /> },
                    { path: 'invoices/:id', element: <InvoiceDetailPage /> },
                    { path: 'reports/sales-register', element: <SalesRegisterPage /> },
                    { path: 'reports/client-outstanding', element: <ClientOutstandingPage /> },
                    {
                      path: 'reports/supplier-paid-outstanding',
                      element: <SupplierPaidOutstandingPage />,
                    },
                    { path: 'reports/balance-due', element: <BalanceDueReportPage /> },
                    { path: 'reports/bank-statement', element: <BankStatementPage /> },
                    { path: 'reports/operation-pending', element: <OperationPendingPage /> },
                    { path: 'reports/deposit-booking', element: <DepositBookingPage /> },
                    { path: 'reports/supplier-payments', element: <SupplierPaymentsPage /> },
                    { path: 'reports/agent-report-card', element: <AgentReportCardPage /> },
                    { path: 'reports/monthly-sale', element: <MonthlySaleReportPage /> },
                    { path: 'reports/bank-reconcile', element: <BankReconcilePage /> },
                    {
                      path: 'reports/supplier-payment-schedule',
                      element: <SupplierPaymentSchedulePage />,
                    },
                    { path: 'reports/supplier-ledger', element: <SupplierLedgerPage /> },
                    { path: 'reports/gst-output-register', element: <GstOutputRegisterPage /> },
                    { path: 'reports/tcs-register', element: <TcsRegisterPage /> },
                    { path: 'reports/credit-notes', element: <CreditNotesListPage /> },
                  ],
                },
                {
                  // Supplier-change approvals: the API is ADMIN-only.
                  element: <RequireRole allow={['ADMIN']} />,
                  children: [
                    {
                      path: 'invoices/supplier-change-approvals',
                      element: <SupplierChangeApprovalsPage />,
                    },
                  ],
                },
                {
                  // User management on the Services app (ADMIN + FINANCE). The
                  // API opens these to FINANCE with an admin-management guardrail;
                  // the permission gate passes for admins and for finance (seed
                  // grants FINANCE sys.users.read). Per-user permission grants +
                  // role CRUD stay CRM/admin-only (not mounted here).
                  element: perm('sys.users.read'),
                  children: [
                    { path: 'users', element: <UsersListPage /> },
                    { path: 'users/:id', element: <UserDetailPage /> },
                  ],
                },
              ]
            : []),
          // ---- CRM-only routes (continued, after the Services invoicing spread) ----
          ...(IS_CRM
            ? [
                {
                  element: perm('itin.coupons.read'),
                  children: [{ path: 'coupons', element: <DiscountCouponsListPage /> }],
                },
                {
                  element: perm('itin.discounts.read'),
                  children: [{ path: 'discounts', element: <DiscountsListPage /> }],
                },
                {
                  element: perm('itin.additional_discounts.read'),
                  children: [
                    { path: 'additional-discounts', element: <AdditionalDiscountsListPage /> },
                  ],
                },
                {
                  element: perm('crm.query_types.read'),
                  children: [{ path: 'query-types', element: <QueryTypesListPage /> }],
                },
                {
                  element: perm('crm.query_sub_sources.read'),
                  children: [{ path: 'query-sub-sources', element: <QuerySubSourcesListPage /> }],
                },
                {
                  element: perm('crm.query_mediums.read'),
                  children: [{ path: 'query-mediums', element: <QueryMediumsListPage /> }],
                },
                {
                  element: perm('crm.query_statuses.read'),
                  children: [{ path: 'query-statuses', element: <QueryStatusesListPage /> }],
                },
                {
                  element: perm('crm.query_sub_statuses.read'),
                  children: [{ path: 'query-sub-statuses', element: <QuerySubStatusesListPage /> }],
                },
                {
                  element: perm('crm.call_statuses.read'),
                  children: [{ path: 'call-statuses', element: <CallStatusesListPage /> }],
                },
                {
                  element: perm('crm.call_sub_statuses.read'),
                  children: [{ path: 'call-sub-statuses', element: <CallSubStatusesListPage /> }],
                },
                {
                  element: perm('crm.lost_reasons.read'),
                  children: [{ path: 'lost-reasons', element: <LostReasonsListPage /> }],
                },
                {
                  // Cost heads master for the Marketing report. Needs the WRITE code:
                  // reading the report never requires this page, and it only changes
                  // what the report can put against a month.
                  element: perm('ops.marketing_reports.write'),
                  children: [{ path: 'marketing-cost-heads', element: <MarketingCostHeadsPage /> }],
                },
                {
                  element: perm('crm.trip_types.read'),
                  children: [{ path: 'trip-types', element: <TripTypesListPage /> }],
                },
                {
                  element: perm('crm.followup_reasons.read'),
                  children: [{ path: 'followup-reasons', element: <FollowupReasonsListPage /> }],
                },
                {
                  element: perm('crm.departments.read'),
                  children: [{ path: 'departments', element: <DepartmentsListPage /> }],
                },
                {
                  element: perm('crm.agent_targets.read'),
                  children: [{ path: 'agent-targets', element: <AgentTargetsListPage /> }],
                },
                {
                  element: perm('web.packages.read'),
                  children: [
                    { path: 'packages', element: <PackagesListPage /> },
                    { path: 'packages/:id', element: <PackageDetailPage /> },
                  ],
                },
                {
                  element: perm('web.blogs.read'),
                  children: [
                    { path: 'blogs', element: <BlogsListPage /> },
                    { path: 'blogs/new', element: <BlogFormPage /> },
                    { path: 'blogs/:id', element: <BlogFormPage /> },
                  ],
                },
                {
                  element: perm('web.blog_categories.read'),
                  children: [{ path: 'categories', element: <BlogCategoriesListPage /> }],
                },
                {
                  element: perm('web.pages.read'),
                  children: [
                    { path: 'pages', element: <PagesListPage /> },
                    { path: 'pages/new', element: <PlaceholderPage title="New page" /> },
                    { path: 'pages/:id', element: <PageEditor /> },
                  ],
                },
                {
                  // 2026-08-04. Career sat inside the `web.pages.read` block above
                  // while its sidebar item is gated on `web.career.read`, so a role
                  // holding only the career grant (the HR desk) saw the menu entry
                  // but RequireRole bounced the click back to the role home - the
                  // link looked dead. Guard now matches nav-config.
                  element: perm('web.career.read'),
                  children: [
                    // Career editor rendered at the stable /career URL (keeps the
                    // Career sidebar item selected). See CareerEditor.
                    { path: 'career', element: <CareerEditor /> },
                  ],
                },
                {
                  element: perm('web.themes.read'),
                  children: [{ path: 'marketing-tags', element: <MarketingTagsListPage /> }],
                },
                {
                  element: perm('web.hero_inclusions.read'),
                  children: [{ path: 'hero-inclusions', element: <HeroInclusionsListPage /> }],
                },
                {
                  element: perm('web.inclusions_exclusions.read'),
                  children: [
                    { path: 'inclusions-exclusions', element: <InclusionsExclusionsListPage /> },
                  ],
                },
                {
                  element: perm('web.navigation.read'),
                  children: [{ path: 'navigation', element: <NavigationPage /> }],
                },
                {
                  element: perm('web.destination_pages.read'),
                  children: [
                    { path: 'destination-pages', element: <DestinationPagesListPage /> },
                    { path: 'destination-pages/:id', element: <DestinationPageDetailPage /> },
                  ],
                },
                {
                  element: perm('web.category_pages.read'),
                  children: [
                    { path: 'category-pages', element: <CategoryPagesListPage /> },
                    { path: 'category-pages/:id', element: <CategoryPageDetailPage /> },
                  ],
                },
                {
                  element: perm('web.tourism_boards.read'),
                  children: [
                    {
                      path: 'tourism-boards',
                      element: <PagesListPage config={TOURISM_BOARDS_CONFIG} />,
                    },
                    {
                      path: 'tourism-boards/:id',
                      element: <PageEditor config={TOURISM_BOARDS_CONFIG} />,
                    },
                  ],
                },
                {
                  element: perm('web.landing_pages.read'),
                  children: [
                    {
                      path: 'landing-pages',
                      element: <PagesListPage config={LANDING_PAGES_CONFIG} />,
                    },
                    {
                      path: 'landing-pages/:id',
                      element: <PageEditor config={LANDING_PAGES_CONFIG} />,
                    },
                  ],
                },
                {
                  element: perm('web.best_time_pages.read'),
                  children: [
                    {
                      path: 'best-time-to-visit',
                      element: <PagesListPage config={BEST_TIME_PAGES_CONFIG} />,
                    },
                    {
                      path: 'best-time-to-visit/:id',
                      element: <PageEditor config={BEST_TIME_PAGES_CONFIG} />,
                    },
                  ],
                },
                {
                  element: perm('web.best_places_pages.read'),
                  children: [
                    {
                      path: 'best-places-to-visit',
                      element: <PagesListPage config={BEST_PLACES_PAGES_CONFIG} />,
                    },
                    {
                      path: 'best-places-to-visit/:id',
                      element: <PageEditor config={BEST_PLACES_PAGES_CONFIG} />,
                    },
                  ],
                },
                {
                  element: perm('sys.notifications.read'),
                  children: [{ path: 'notifications', element: <NotificationsPage /> }],
                },
                {
                  element: perm('sys.secrets.read'),
                  children: [{ path: 'secrets', element: <SecretsListPage /> }],
                },
                {
                  element: perm('sys.meta_capi.read'),
                  children: [{ path: 'meta-capi', element: <MetaCapiPage /> }],
                },
                {
                  element: perm('sys.users.read'),
                  children: [
                    { path: 'users', element: <UsersListPage /> },
                    { path: 'users/:id', element: <UserDetailPage /> },
                  ],
                },
                {
                  element: perm('sys.roles.read'),
                  children: [{ path: 'roles', element: <RolesListPage /> }],
                },

                // Customer Itinerary Link. 2026-07-09: gate matches the sidebar
                // (`ops.customer_pdf.read`) so URL-hop access aligns with menu
                // visibility. Admin can grant this to other roles from the
                // /roles matrix without a code change. API writes remain
                // role-gated server-side as defence-in-depth.
                {
                  element: <RequireRole permission="ops.customer_pdf.read" />,
                  children: [{ path: 'customer-pdf', element: <CustomerPdfPage /> }],
                },

                // Legacy placeholders - no nav item yet; authenticated only.
                { path: 'media', element: <PlaceholderPage title="Media" /> },
                { path: 'destinations', element: <PlaceholderPage title="Destinations" /> },
                { path: 'partners', element: <PlaceholderPage title="Partners" /> },
                { path: 'press', element: <PlaceholderPage title="Press" /> },
              ]
            : []),

          // Unknown path: on the Services build bounce to Invoices (no CRM screen
          // is mounted there, so a stray CRM URL resolves here); on the CRM build
          // show the in-app 404.
          {
            path: '*',
            element: IS_SERVICES ? <Navigate to="/invoices" replace /> : <RouteError />,
          },
        ],
      },
    ],
  },
]);

export const AppRouter = (): React.JSX.Element => <RouterProvider router={router} />;
